import React from 'react';
import { CalendarRange, Info, X } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../../components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../../../components/ui/popover';
import { DATE_PRESETS, WEEKDAYS } from './metrics';

const SEGMENTS = [
  { value: 'all', label: 'All clients' },
  { value: 'gr', label: 'Guaranteed Returns only' },
  { value: 'traditional', label: 'Traditional wholesale only' },
];

const MultiPicker = ({ label, options, selected, onChange, testId, emptyLabel }) => {
  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-9 justify-start font-normal" data-testid={testId}>
          {selected.length === 0 ? emptyLabel : `${selected.length} ${label.toLowerCase()} selected`}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="max-h-64 overflow-y-auto p-2 space-y-1">
          {(options || []).map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => toggle(o.id)}
              className={`w-full text-left text-sm px-2 py-1.5 rounded transition-colors ${
                selected.includes(o.id) ? 'bg-amber-100 text-amber-900' : 'hover:bg-gray-100'
              }`}
              data-testid={`${testId}-option-${o.id}`}
            >
              {o.name}
              {o.has_guaranteed_returns === true && (
                <span className="ml-2 text-xs text-emerald-700">GR</span>
              )}
            </button>
          ))}
        </div>
        {selected.length > 0 && (
          <div className="border-t p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => onChange([])}
              data-testid={`${testId}-clear`}
            >
              <X className="h-3 w-3 mr-1" /> Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export const FilterBar = ({ filters, setFilters, options, completeness }) => {
  const patch = (next) => setFilters((prev) => ({ ...prev, ...next }));

  const applyPreset = (key) => {
    const preset = DATE_PRESETS.find((p) => p.key === key);
    if (!preset || !preset.range) {
      patch({ preset: key });
      return;
    }
    patch({ preset: key, ...preset.range() });
  };

  const selectedClients = filters.clientIds || [];
  const clientOptions = (options?.clients || []).map((c) => ({
    id: c.id, name: c.name, has_guaranteed_returns: c.has_guaranteed_returns,
  }));
  const productOptions = (options?.products || []).map((p) => ({ id: p.id, name: p.name }));

  return (
    <Card data-testid="client-performance-filters">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[170px]">
            <Label className="text-xs text-gray-500">Period</Label>
            <Select value={filters.preset} onValueChange={applyPreset}>
              <SelectTrigger className="h-9 mt-1" data-testid="filter-preset">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_PRESETS.map((p) => (
                  <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-500">From</Label>
            <Input
              type="date"
              value={filters.start}
              onChange={(e) => patch({ start: e.target.value, preset: 'custom' })}
              className="h-9 mt-1 w-40"
              data-testid="filter-start"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500">To</Label>
            <Input
              type="date"
              value={filters.end}
              onChange={(e) => patch({ end: e.target.value, preset: 'custom' })}
              className="h-9 mt-1 w-40"
              data-testid="filter-end"
            />
          </div>
          <div className="min-w-[200px]">
            <Label className="text-xs text-gray-500">Terms</Label>
            <Select value={filters.segment} onValueChange={(v) => patch({ segment: v })}>
              <SelectTrigger className="h-9 mt-1" data-testid="filter-segment">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SEGMENTS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <Label className="text-xs text-gray-500">Pricing tier</Label>
            <Select
              value={filters.tierId || 'all'}
              onValueChange={(v) => patch({ tierId: v === 'all' ? '' : v })}
            >
              <SelectTrigger className="h-9 mt-1" data-testid="filter-tier">
                <SelectValue placeholder="All tiers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tiers</SelectItem>
                {(options?.tiers || []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <Label className="text-xs text-gray-500 mb-1">Clients</Label>
            <MultiPicker
              label="Clients"
              options={clientOptions}
              selected={selectedClients}
              onChange={(v) => patch({ clientIds: v })}
              testId="filter-clients"
              emptyLabel="All clients"
            />
          </div>
          <div className="flex flex-col">
            <Label className="text-xs text-gray-500 mb-1">Products</Label>
            <MultiPicker
              label="Products"
              options={productOptions}
              selected={filters.productIds || []}
              onChange={(v) => patch({ productIds: v })}
              testId="filter-products"
              emptyLabel="All products"
            />
          </div>
          <div className="min-w-[150px]">
            <Label className="text-xs text-gray-500">Category</Label>
            <Select
              value={filters.categories?.[0] || 'all'}
              onValueChange={(v) => patch({ categories: v === 'all' ? [] : [v] })}
            >
              <SelectTrigger className="h-9 mt-1" data-testid="filter-category">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {(options?.categories || []).map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[150px]">
            <Label className="text-xs text-gray-500">Delivery weekday</Label>
            <Select
              value={filters.weekday === '' ? 'all' : String(filters.weekday)}
              onValueChange={(v) => patch({ weekday: v === 'all' ? '' : v })}
            >
              <SelectTrigger className="h-9 mt-1" data-testid="filter-weekday">
                <SelectValue placeholder="Every day" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Every day</SelectItem>
                {WEEKDAYS.map((w) => (
                  <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1 border-t">
          <CalendarRange className="h-4 w-4 text-gray-400" />
          <span className="text-xs text-gray-600" data-testid="completeness-note">
            {filters.start} → {filters.end}
          </span>
          {completeness && (
            <Badge
              variant="outline"
              className={completeness.range_incomplete
                ? 'text-amber-800 border-amber-300 bg-amber-50'
                : 'text-gray-600'}
              data-testid="completeness-badge"
            >
              <Info className="h-3 w-3 mr-1" />
              Data final through {completeness.complete_through}
            </Badge>
          )}
          {completeness?.range_incomplete && (
            <span className="text-xs text-amber-700">
              Part of this range is still settling — don&apos;t read it as a decline.
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FilterBar;
